/**
 * SEO string helpers — client-side templates that mirror the backend's
 * routers_seo.py exactly. Used by react-helmet-async on every page so
 * the head stays coherent after client-side navigation. The initial
 * document render is already meta-injected by the backend (dev-server
 * middleware in preview; hosting rewrite in prod).
 *
 * Rules cross-referenced in owner spec:
 *   - Card title: `{Card Name} {display-number} · {Set Name} · {Variant} | CardCapture`
 *     (omit variant clause for default printing)
 *   - Card description: ≤~155 chars, drops missing clauses.
 *   - Set title / desc: as specified in §2.
 *   - Home / language pick / sets list / static pages: as specified.
 *
 * Never surfaces `undefined` — every helper coerces / drops empty pieces.
 */

import { formatCardNumber } from "./cardNumber";
import { slugify } from "./slug";

export const SITE_NAME = "CardCapture";
export const DESC_MAX = 158;

const DEFAULT_OG_IMAGE = "/brand/og-image.png"; // absolutised in headHelpers()

function clip(text, limit = DESC_MAX) {
  if (!text) return "";
  if (text.length <= limit) return text;
  const trimmed = text.slice(0, limit).replace(/\s+\S*$/, "");
  return trimmed.replace(/[,.;·:—\- ]+$/, "") + "…";
}

function absolutise(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (typeof window === "undefined") return u;
  return new URL(u, window.location.origin).toString();
}

/**
 * Return canonical head props for `react-helmet-async`.
 * Every caller passes the raw fields; we handle absolutisation +
 * fallbacks + Twitter/OG parity.
 */
export function baseHead({
  title,
  description,
  canonicalPath,
  ogImage,
  ogType = "website",
  robots = "index, follow",
}) {
  const canonical =
    typeof window !== "undefined" && canonicalPath
      ? new URL(canonicalPath, window.location.origin).toString()
      : canonicalPath || "";
  const image = absolutise(ogImage || DEFAULT_OG_IMAGE);
  return {
    title,
    description: clip(description),
    canonical,
    ogImage: image,
    ogType,
    robots,
  };
}

/**
 * Home page.
 */
export function homeMeta() {
  return baseHead({
    title: `${SITE_NAME} — see trading cards as they really look`,
    description:
      "Community photos and videos showing real texture, holo, and finish " +
      "that flat scans miss. Reference library for Pokémon TCG collectors.",
    canonicalPath: "/",
  });
}

export function languagePickMeta(game = "pokemon") {
  const label = game === "pokemon" ? "Pokémon" : game;
  return baseHead({
    title: `${label} — choose language | ${SITE_NAME}`,
    description: `Pick a language catalog to browse ${label} TCG sets and cards on ${SITE_NAME}.`,
    canonicalPath: `/${game}`,
  });
}

export function setsListMeta() {
  return baseHead({
    title: `Pokémon English Sets | ${SITE_NAME}`,
    description:
      "Browse every English Pokémon set with card counts, release dates, " +
      "market prices, and community photos on CardCapture.",
    canonicalPath: "/pokemon/en/sets",
  });
}

export function setDetailMeta(setDoc) {
  if (!setDoc) return setsListMeta();
  const name = setDoc.name || "";
  const cardCount = Number(setDoc.card_count || 0);
  const year =
    typeof setDoc.release_date === "string" && setDoc.release_date.length >= 4
      ? setDoc.release_date.slice(0, 4)
      : null;
  const yearClause = year ? ` (${year})` : "";
  return baseHead({
    title: `${name} — all cards, prices & photos | ${SITE_NAME}`,
    description:
      `Browse all ${cardCount} cards from ${name}${yearClause} with market ` +
      "prices and community photos on CardCapture.",
    canonicalPath: `/pokemon/en/sets/${slugify(name)}`,
  });
}

export function cardMeta({ setDoc, card, variant, defaultVariantSlug }) {
  if (!setDoc || !card || !variant) return homeMeta();
  const setName = setDoc.name || "";
  const cardName = card.name || "";
  const number = card.number || "";
  const rarity = card.rarity || "";
  const variantSlug = variant.slug || variant.variant_type || "";
  const isDefault = defaultVariantSlug && variantSlug === defaultVariantSlug;
  const variantLabel = variant.variant_label; // null for normal
  const variantClause = isDefault || !variantLabel ? "" : ` · ${variantLabel}`;

  const displayNumber = formatCardNumber({
    localId: number,
    cardCountOfficial: setDoc.card_count_official,
    setCode: setDoc.code || setDoc.external_id,
  });

  const titleHead = displayNumber ? `${cardName} ${displayNumber}` : cardName;
  const title = `${titleHead} · ${setName}${variantClause} | ${SITE_NAME}`;

  const numberDisplay = displayNumber ? displayNumber.split("/")[0] : number;
  const detailBits = [];
  if (rarity) detailBits.push(rarity);
  if (variantLabel) detailBits.push(variantLabel);
  const detailClause = detailBits.length ? ` — ${detailBits.join(", ")}` : "";
  const description =
    `See real collector photos of ${cardName}` +
    (numberDisplay ? ` (${numberDisplay})` : "") +
    ` from ${setName}${detailClause}. High-quality images showing the card's true texture and finish on ${SITE_NAME}.`;

  const urlNumber = encodeURIComponent(String(number).toLowerCase());
  const base = `/pokemon/en/sets/${slugify(setName)}/${slugify(cardName)}/${urlNumber}`;
  const canonicalPath = isDefault ? base : `${base}/${encodeURIComponent(variantSlug)}`;

  return baseHead({
    title,
    description,
    canonicalPath,
    ogImage: card.reference_image_url || undefined,
  });
}

export function staticMeta(slug) {
  const map = {
    about: {
      title: `About | ${SITE_NAME}`,
      description:
        "CardCapture is a community reference library for TCG collectors — " +
        "real photos of real cards, showing texture and holo that flat scans miss.",
    },
    privacy: {
      title: `Privacy Policy | ${SITE_NAME}`,
      description:
        "How CardCapture collects and stores data from Google sign-in, submissions, and browser storage.",
    },
    terms: {
      title: `Terms of Service | ${SITE_NAME}`,
      description:
        "Acceptable use, moderation model, and upload license grant for CardCapture contributors.",
    },
    leaderboard: {
      title: `Leaderboard | ${SITE_NAME}`,
      description:
        "Top collector contributors ranked by community upvotes on CardCapture.",
    },
  };
  const cfg = map[slug];
  if (!cfg) return homeMeta();
  return baseHead({
    title: cfg.title,
    description: cfg.description,
    canonicalPath: `/${slug}`,
  });
}

/**
 * JSON-LD helpers.
 */
export function breadcrumbList({ origin, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path
        ? typeof window !== "undefined"
          ? new URL(it.path, origin || window.location.origin).toString()
          : it.path
        : undefined,
    })),
  };
}

export function productLD({ setDoc, card, variant, defaultVariantSlug, meta }) {
  const variantSlug = variant.slug || variant.variant_type || "";
  const isDefault = defaultVariantSlug && variantSlug === defaultVariantSlug;
  const variantLabel = variant.variant_label;
  const nameSuffix = isDefault || !variantLabel ? "" : ` · ${variantLabel}`;

  const obj = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: (card.name || "") + nameSuffix,
    description: meta.description,
    url: meta.canonical,
  };
  if (meta.ogImage) obj.image = meta.ogImage;
  if (setDoc?.external_id && card?.number) {
    obj.sku = `${setDoc.external_id}-${card.number}-${variantSlug}`;
  }
  const priceRaw =
    variant.market_price != null ? variant.market_price : variant.admin_substitute_price;
  const price = typeof priceRaw === "number" ? priceRaw : Number(priceRaw);
  if (Number.isFinite(price) && price > 0) {
    obj.offers = {
      "@type": "Offer",
      priceCurrency: "USD",
      price: price.toFixed(2),
      url: meta.canonical,
      availability: "https://schema.org/InStock",
    };
  }
  return obj;
}


/**
 * Showcase page meta.
 *
 * Owner spec (2026-02-01 showcase pass):
 *   Title: `{Card Name} {display-number} — showcase by {collector} · {Set Name} | CardCapture`
 *   Description: `A real collector photo/video of {Card Name} ({number}) from {Set Name},
 *       showing its true {variant} texture and finish — shared by {collector} on CardCapture.`
 *
 * Image submissions:  og:image = submission image (never the reference)
 * Video submissions:  og:type=video.other, og:video:* fields, og:image = poster
 * Both:               emit both. Image is the primary og:image; VideoObject is included.
 */
export function showcaseMeta(payload) {
  if (!payload || !payload.card || !payload.set || !payload.variant) {
    return homeMeta();
  }
  const { card, set, variant, default_variant_slug: defaultSlug } = payload;
  const setName = set.name || "";
  const cardName = card.name || "";
  const number = card.number || "";
  const variantSlug = variant.slug || variant.variant_type || "";
  const variantLabel = variant.variant_label || "";
  const displayNumber = formatCardNumber({
    localId: number,
    cardCountOfficial: set.card_count_official,
    setCode: set.code || set.external_id,
  });
  const collector =
    payload.user?.username ||
    payload.user?.display_name ||
    payload.user_display_name ||
    "Collector";

  const titleNumber = displayNumber ? ` ${displayNumber}` : "";
  const title = `${cardName}${titleNumber} — showcase by ${collector} · ${setName} | ${SITE_NAME}`;

  const hasImage = !!payload.image;
  const hasVideo = !!(payload.video && payload.video.transcode_status === "ready");
  const mediaWord = hasImage && !hasVideo ? "photo" : hasVideo && !hasImage ? "video" : "photo/video";
  const numberDisplay = displayNumber ? displayNumber.split("/")[0] : number;
  const variantClause = variantLabel ? variantLabel.toLowerCase() : "normal";
  const description =
    `A real collector ${mediaWord} of ${cardName}` +
    (numberDisplay ? ` (${numberDisplay})` : "") +
    ` from ${setName}, showing its true ${variantClause} texture and finish — shared by ${collector} on ${SITE_NAME}.`;

  const urlNumber = encodeURIComponent(String(number).toLowerCase());
  const isDefault = defaultSlug && variantSlug === defaultSlug;
  const cardBase =
    `/pokemon/en/sets/${slugify(setName)}/${slugify(cardName)}/${urlNumber}` +
    (isDefault ? "" : `/${encodeURIComponent(variantSlug)}`);
  const canonicalPath = `${cardBase}/showcase/${encodeURIComponent(payload.id)}`;

  // Prefer the submission's own image; otherwise poster; otherwise brand fallback.
  const ogImage = payload.image?.url || payload.video?.poster_url || undefined;
  const head = baseHead({
    title,
    description,
    canonicalPath,
    ogImage,
    ogType: hasVideo && !hasImage ? "video.other" : "website",
  });

  // Extend with video open-graph fields when applicable.
  if (hasVideo && payload.video) {
    head.videoUrl = payload.video.mp4_url || payload.video.preview_url || null;
    head.videoType = payload.video.content_type || "video/mp4";
    head.videoWidth = payload.video.width || null;
    head.videoHeight = payload.video.height || null;
  }
  return head;
}

/**
 * JSON-LD for a showcase page. Returns an array — the client Helmet
 * renders one <script> per entry.
 *   - ImageObject with width/height/caption when image present
 *   - VideoObject with duration (ISO 8601) + thumbnail + content URL when video present
 *   - Product tying the submission to the parent card
 */
export function showcaseJsonLD(payload, meta) {
  if (!payload || !payload.card || !payload.set || !payload.variant) return [];
  const { card, set, variant } = payload;
  const collectorName =
    payload.user?.username ||
    payload.user?.display_name ||
    payload.user_display_name ||
    "Collector";
  const creator = {
    "@type": "Person",
    name: collectorName,
    url:
      typeof window !== "undefined"
        ? new URL(`/users/${payload.user_id}`, window.location.origin).toString()
        : `/users/${payload.user_id}`,
  };
  const out = [];

  if (payload.image) {
    const img = {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      contentUrl: payload.image.url,
      thumbnailUrl: payload.image.thumbnail_url || payload.image.url,
      caption: `${card.name} ${card.number}${variant.variant_label ? ` ${variant.variant_label}` : ""} — collector showcase`,
      creator,
    };
    if (payload.image.width) img.width = payload.image.width;
    if (payload.image.height) img.height = payload.image.height;
    if (payload.created_at) img.uploadDate = payload.created_at;
    out.push(img);
  }

  if (payload.video && payload.video.transcode_status === "ready") {
    const vid = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: `${card.name}${variant.variant_label ? ` · ${variant.variant_label}` : ""} — collector showcase`,
      description: meta.description,
      thumbnailUrl: payload.video.poster_url,
      contentUrl: payload.video.mp4_url || payload.video.preview_url,
      uploadDate: payload.created_at,
      creator,
    };
    // duration as ISO 8601 (PT<seconds>S)
    if (typeof payload.video.duration_ms === "number" && payload.video.duration_ms > 0) {
      vid.duration = `PT${Math.round(payload.video.duration_ms / 1000)}S`;
    }
    if (payload.video.width) vid.width = payload.video.width;
    if (payload.video.height) vid.height = payload.video.height;
    out.push(vid);
  }

  // Product tying the showcase to the parent card
  const variantSlug = variant.slug || variant.variant_type || "";
  const variantLabel = variant.variant_label;
  const isDefault = payload.default_variant_slug && variantSlug === payload.default_variant_slug;
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: card.name + (isDefault || !variantLabel ? "" : ` · ${variantLabel}`),
    description: `Community showcase page on ${SITE_NAME}.`,
    url: meta.canonical,
    sku: `${set.external_id}-${card.number}-${variantSlug}`,
  };
  if (meta.ogImage) product.image = meta.ogImage;
  out.push(product);

  return out;
}